import React, { Fragment, useEffect } from 'react';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import PageHeader from '@components/ssap/headers/PageHeader';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';
import SourceSummary from './pageIncludes/summary/SourceSummary';
import UniversalInput from '@lib/wrappers/UniversalInput';

// ActionCreators

import { changeData, clearMethods, setMethods } from '@actions/ssap/ssapActionCreators';
import { showModal } from '@actions/common/actionCreators';

// Hooks

import { useInternationalizedOptions } from '@hooks/useInternationalizedOptions';
import { useIfMounted } from '@hooks/useIfMounted';

// Utils

import { getArrayOfObjectsToPrimitive } from '@utils/miscellaneous/getArrayOfObjectsToPrimitive';
import { internationalizeList } from '@utils/miscellaneous/internationalizeList';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '@utils/tools/preventerTools';
import { filterTools } from '@utils/tools/filterTools';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { INCOME_SOURCES_SUMMARY_GROUPING } from '@constants/ssap/miscellaneous/miscellaneous';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { ssapModalTypes as MODAL_TYPES } from '@constants/ssap/modals/ssapModalTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';


const Page3_32_E1 = props => {

    const {
        data,
        data: {
            coverageYear
        },
        form,
        formName,
        householdMembers,
        methods,
        page
    } = props;

    const {
        isFormOnTopLevelValid,
        areAllInputsValid,
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    const {
        buttonContainer: ButtonContainer
    } = props;

    const {
        hhmIndex: member,
        currentHhm
    } = props;

    const {
        applicantGuid,
        incomes,
        hhmIncomeParams: {
            hasAnyIncome
        },
        name
    } = currentHhm;

    const isMount = useIfMounted();

    useEffect(() => {
        if(isMount) {
            methods[formName]
                .onFormSubmit(
                    page,
                    null,
                    null,
                    data
                );
        }
    }, [ getArrayOfObjectsToPrimitive(incomes) ]);

    const handleIncomeSourceDeletion = (member, incomeIndex) => {

        const newState = { ...data };

        const [{ sourceName }] = newState
            .taxHousehold
            .householdMember[member]
            .incomes
            .splice(incomeIndex, 1);

        newState
            .taxHousehold
            .householdMember = newState
                .taxHousehold
                .householdMember.map(hhm => {
                    return {
                        ...hhm,
                        employerSponsoredCoverage: hhm.employerSponsoredCoverage
                            .reduce((coverages, coverage) => {
                                if(coverage.applicantGuid === applicantGuid && coverage.companyName === sourceName) {
                                    return [ ...coverages ];
                                } else {
                                    return [ ...coverages, coverage ];
                                }
                            }, []),
                        healthReimbursementArrangement: hhm.healthReimbursementArrangement
                            .reduce((hras, hra) => {
                                if(hra.applicantGuid === applicantGuid && hra.employerName === sourceName) {
                                    return [ ...hras ];
                                } else {
                                    return [ ...hras, hra ];
                                }
                            }, [])
                    };
                });

        const isNoIncome = newState
            .taxHousehold
            .householdMember[member]
            .incomes
            .filter(filterTools.removeDummyType)
            .length === 0;

        if (isNoIncome) {
            newState
                .taxHousehold
                .householdMember[member]
                .hhmIncomeParams
                .hasAnyIncome = null;
        }

        props.actions.changeData(newState);
    };

    const handleIncomeSourceEditing = incomeIndex => {

        props.actions.showModal({
            modalType: MODAL_TYPES.ADD_INCOME_OR_DEDUCTION_SOURCE_MODAL,
            modalData: {
                source: incomes[incomeIndex],
                index: incomeIndex,
                parameter: 'incomes',
                pageToReturn: page
            }
        });
    };

    const handleAddIncomeSourceClick = () => {
        props.actions.showModal({
            modalType: MODAL_TYPES.ADD_INCOME_OR_DEDUCTION_SOURCE_MODAL,
            modalData: {
                source: {},
                index: null,
                parameter: 'incomes',
                pageToReturn: page
            }
        });
    };

    useEffect(() => {
        props.actions.setMethods('Page3_32_E1',
            {
                handleIncomeSourceDeletion,
                handleIncomeSourceEditing,
                handleAddIncomeSourceClick
            });

        return () => {
            props.actions.clearMethods('Page3_32_E1');
        };
    }, [ incomes.map(income => Object.values(income).toLocaleString()).toLocaleString() ]);

    const onFieldChange = (...args) => {
        const [ newData, index ] = args;

        const newState = { ...data };

        newState.taxHousehold.householdMember[index] = newData;

        props.actions.changeData(newState);
    };

    const checkIfDisabled = () => incomes.filter(filterTools.removeDummyType).length > 0 ? false : !hasAnyIncome;

    const checkTopLevelValidation = () => {
        switch(true) {
            case hasAnyIncome === true:
                return incomes.filter(filterTools.removeDummyType).length > 0;
            case hasAnyIncome === false:
                return true;
            case hasAnyIncome === null:
                return false;
        }
    };

    const getIncomeSources = () => {
        return INCOME_SOURCES_SUMMARY_GROUPING.map((groupedIds, i) => (
            <div
                className = 'mobile:grid-col-12 tablet:grid-col-4'
                key = { i }
            >
                {
                    internationalizeList(groupedIds, 'p')
                }
            </div>
        ));
    };

    const dropdownSourcesList = useInternationalizedOptions(DROPDOWN_OPTIONS.INCOME_SOURCES);
    const dropdownFrequencyList = useInternationalizedOptions(DROPDOWN_OPTIONS.FREQUENCY);

    return (
        <Fragment>
            <div id = 'iiIncomeSources' >
                <div className = 'subsection' >
                    <PageHeader
                        text = 'ssap.subheader.income_of_person'
                        textReplacements = { {
                            personName: normalizeNameOfPerson(name)
                        } }
                        headerClassName = 'gi-heading-alt'
                    />
                    <FormattedMessage
                        id = 'ssap.income_information.income_sources.notification'
                        values = { {
                            coverageYear
                        } }
                        tagName = 'p'
                    />
                    <div className = 'grid-row' >
                        { getIncomeSources() }
                    </div>

                    <Form
                        topLevelValidity = { checkTopLevelValidation() }
                        { ...{ formName } }
                    >

                        {
                            incomes.filter(filterTools.removeDummyType).length === 0
                                ? (
                                    <div className = 'margin-tb-20' >

                                        <FormattedMessage
                                            id = 'ssap.income_information.income_sources.enter_all'
                                            tagName='p'
                                        />

                                        <ValidationErrorMessage
                                            isVisible = { isFormSubmitted && !isFormOnTopLevelValid && areAllInputsValid }
                                            messageText = { VALIDATION_ERROR_MESSAGES.INCOME_SOURCE }
                                            errorClasses = { [ 'margin-l-0' ] }
                                        />

                                        <UniversalInput
                                            legend = { {
                                                legendText: 'ssap.input.legend.does_get_income',
                                                legendTextReplacements: {
                                                    personName: normalizeNameOfPerson(name)
                                                }
                                            } }
                                            label = { {
                                                labelClass: [ 'margin-t-0' ],
                                                showRequiredIcon: true
                                            } }
                                            inputData = { {
                                                isInputRequired: true,
                                                type: INPUT_TYPES.RADIO_BUTTONS,
                                                name: 'hasAnyIncome',
                                                value: hasAnyIncome,
                                                options: DROPDOWN_OPTIONS.YES_NO,
                                                validationType: VALIDATION_TYPES.CHOSEN,
                                                currentData: currentHhm,
                                                fields: [ 'hhmIncomeParams' ]
                                            } }
                                            inputActions = { {
                                                onFieldChange
                                            } }
                                            indexInArray = { member }
                                            errorClasses = { [ 'margin-l-0' ] }
                                        />
                                    </div>
                                )
                                : (
                                    <div className = 'margin-tb-20' >
                                        <p>
                                            <FormattedMessage
                                                id = 'ssap.income_information.income_sources.add_income'
                                                tagName = 'strong'
                                            />
                                        </p>
                                        <SourceSummary
                                            buttonContainer = { ButtonContainer }
                                            sources = { incomes }
                                            member = { member }
                                            dropdownSourcesList = { dropdownSourcesList }
                                            dropdownFrequencyList = { dropdownFrequencyList }
                                            summaryType = 'common.income'
                                        />
                                    </div>
                                )
                        }
                    </Form>

                    <ButtonContainer
                        customButtonClassName = { checkIfDisabled() ? 'btn-disabled' : '' }
                        type = 'addIncomeSourceButton'
                        args = { [ householdMembers, member ] }
                        isDisabled = { checkIfDisabled() }
                    />
                </div>
            </div>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    data: state.ssap.data,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,
    
    methods: state.ssap.methods,
    page: state.ssap.page
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData,
        clearMethods,
        setMethods,
        showModal
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page3_32_E1);
